import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../context/PageTitleContext';
import { Container, Form, Button, Alert, Card, Tabs, Tab } from 'react-bootstrap';
import { safeNext } from '../utils/safeNext';

function Login() {
  const { setPageTitle } = usePageTitle();
  const [searchParams] = useSearchParams();
  // `?next=` brings the user back where they came from, e.g. an invite link (#358);
  // `?tab=register` opens straight on Sign Up for someone new.
  const next = safeNext(searchParams.get('next')) || '/';
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'register' ? 'register' : 'login');

  // Set page title for Login page
  useEffect(() => {
    setPageTitle('Login');
  }, [setPageTitle]);

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Register form state
  const [registerFormData, setRegisterFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
  });
  const [registerError, setRegisterError] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    const result = await login(loginEmail, loginPassword);

    if (result.success) {
      navigate(next);
    } else {
      setLoginError(result.error);
    }

    setLoginLoading(false);
  };

  const handleRegisterChange = (e) => {
    setRegisterFormData({
      ...registerFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setRegisterError('');

    if (registerFormData.password !== registerFormData.confirmPassword) {
      setRegisterError('Passwords do not match');
      return;
    }

    if (registerFormData.password.length < 6) {
      setRegisterError('Password must be at least 6 characters');
      return;
    }

    if (!termsAccepted) {
      setRegisterError('You must agree to the Terms of Use to create an account');
      return;
    }

    setRegisterLoading(true);

    const { confirmPassword, ...registerData } = registerFormData;
    const result = await register(registerData);

    if (result.success) {
      navigate(next);
    } else {
      setRegisterError(result.error);
    }

    setRegisterLoading(false);
  };

  return (
    <Container className="mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <Card>
            <Card.Body>
              <h2 className="text-center mb-4">Welcome to dyner.z</h2>
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-3"
                justify
              >
                <Tab eventKey="login" title="Login">
                  {loginError && <Alert variant="danger">{loginError}</Alert>}
                  <Form onSubmit={handleLoginSubmit}>
                    <Form.Group className="mb-3" controlId="loginEmail">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="loginPassword">
                      <Form.Label>Password</Form.Label>
                      <Form.Control
                        type="password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        required
                      />
                    </Form.Group>

                    <Button variant="primary" type="submit" disabled={loginLoading} className="w-100">
                      {loginLoading ? 'Logging in...' : 'Login'}
                    </Button>
                  </Form>
                  <div className="text-center mt-3">
                    <Link to="/forgot-password">Forgot Password?</Link>
                  </div>
                </Tab>

                <Tab eventKey="register" title="Register">
                  {registerError && <Alert variant="danger">{registerError}</Alert>}
                  <Form onSubmit={handleRegisterSubmit}>
                    <Form.Group className="mb-3" controlId="registerEmail">
                      <Form.Label>Email *</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={registerFormData.email}
                        onChange={handleRegisterChange}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="registerFirstName">
                      <Form.Label>First Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="first_name"
                        value={registerFormData.first_name}
                        onChange={handleRegisterChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="registerLastName">
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="last_name"
                        value={registerFormData.last_name}
                        onChange={handleRegisterChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="registerPassword">
                      <Form.Label>Password *</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        value={registerFormData.password}
                        onChange={handleRegisterChange}
                        required
                      />
                      <Form.Text className="text-muted">
                        Must be at least 6 characters
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="registerConfirmPassword">
                      <Form.Label>Confirm Password *</Form.Label>
                      <Form.Control
                        type="password"
                        name="confirmPassword"
                        value={registerFormData.confirmPassword}
                        onChange={handleRegisterChange}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="registerTerms">
                      <Form.Check
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        label={
                          <>
                            I agree to the{' '}
                            <Link to="/terms" target="_blank" rel="noopener noreferrer">
                              Terms of Use
                            </Link>{' '}
                            (zero tolerance for objectionable content)
                          </>
                        }
                      />
                    </Form.Group>

                    <Button variant="primary" type="submit" disabled={registerLoading} className="w-100">
                      {registerLoading ? 'Creating account...' : 'Register'}
                    </Button>
                  </Form>
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </div>
      </div>
    </Container>
  );
}

export default Login;
